import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Warning, Phone, LinkSimple, CheckCircle, Sparkle } from "@phosphor-icons/react";

/**
 * JobCoverageBanner — polls the coverage-check endpoint on job load and
 * shows a bright red banner if any policy applies. Includes a "File claim"
 * button + "Assign warranty vendor" one-click.
 *
 * Also acts as the clock-in gate signal — parent JobDetail should refuse to
 * allow tech clock-in when applicablePolicies exist AND no claim is on file.
 */
export default function JobCoverageBanner({ jobId, onClaimFiled, onWarrantyAssigned }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filingClaim, setFilingClaim] = useState(null); // policy object

  const load = async () => {
    if (!jobId) return;
    setBusy(true);
    try {
      const r = await api.get(`/insurance-policies/coverage-check?job_id=${jobId}&include_ai=true`);
      setData(r.data);
    } catch (e) {
      // fail-silent — banner is optional info
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [jobId]);

  if (!data || (!data.policies?.length && !data.claim_on_file)) return null;

  const ai = data.ai_opinion || {};
  const bestId = ai.best_policy_id;
  const bestPolicy = data.policies.find(p => p.id === bestId);
  const primary = bestPolicy || data.policies[0];

  const assignWarrantyVendor = async (policyId) => {
    try {
      const r = await api.post(`/jobs/${jobId}/assign-warranty-vendor?policy_id=${policyId}`);
      toast.success(`Assigned to ${r.data.warranty_vendor.name}`);
      onWarrantyAssigned && onWarrantyAssigned(r.data.warranty_vendor);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };

  // Already have a claim on file → GREEN success banner
  if (data.claim_on_file) {
    const c = data.insurance_claim || {};
    return (
      <div className="p-3 bg-emerald-50 border-4 border-emerald-500 mb-3" data-testid="coverage-claim-filed-banner">
        <div className="flex items-start gap-2">
          <CheckCircle weight="fill" className="text-emerald-600 shrink-0 mt-0.5" size={20}/>
          <div className="flex-1 text-xs">
            <div className="font-black uppercase tracking-widest text-emerald-900">
              Insurance claim on file · {c.provider_name}
            </div>
            <div className="mt-1 text-slate-700">
              Claim #<b>{c.claim_number || "(pending)"}</b> · Status: <b>{c.status}</b>
              {c.filed_by_name && <> · Filed by {c.filed_by_name}</>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-red-50 border-4 border-red-500 mb-3 space-y-2" data-testid="coverage-warning-banner">
      <div className="flex items-start gap-2">
        <Warning weight="fill" className="text-red-600 shrink-0 mt-0.5" size={22}/>
        <div className="flex-1">
          <div className="font-black uppercase tracking-widest text-red-900 text-sm">
            🛡️ Insurance / Warranty Coverage May Apply
          </div>
          <div className="text-[11px] text-slate-700 mt-1">
            <b>File the claim before dispatching a tech.</b> Tech clock-in is blocked
            until a claim number is on file or the coverage is marked resolved.
          </div>
        </div>
      </div>

      {ai.reasoning && bestPolicy && (
        <div className="p-2 bg-white border-2 border-fuchsia-300 text-xs" data-testid="ai-coverage-opinion">
          <div className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700 flex items-center gap-1">
            <Sparkle weight="fill" size={10}/> AI opinion (confidence: {Math.round((ai.confidence || 0) * 100)}%)
          </div>
          <div className="mt-1 text-slate-700">{ai.reasoning}</div>
          <div className="mt-1 font-black text-slate-900">→ {bestPolicy.provider_name}</div>
        </div>
      )}

      <div className="space-y-2">
        {data.policies.map(p => {
          const isBest = p.id === bestId;
          return (
            <div key={p.id} data-testid={`policy-option-${p.id}`}
              className={`p-2 border-2 ${isBest ? "border-fuchsia-500 bg-fuchsia-50" : "border-slate-200 bg-white"} text-xs`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-black">{p.provider_name} {isBest && <span className="text-[9px] text-fuchsia-700">· AI PICK</span>}</div>
                  <div className="text-[10px] text-slate-600">{p.kind.replace(/_/g, " ")}</div>
                </div>
                <div className="flex gap-2 text-[11px]">
                  {p.claim_phone && (
                    <a href={`tel:${p.claim_phone}`} className="inline-flex items-center gap-1 font-black text-emerald-700 hover:underline">
                      <Phone size={11}/> {p.claim_phone}
                    </a>
                  )}
                  {p.claim_portal_url && (
                    <a href={p.claim_portal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-700 hover:underline">
                      <LinkSimple size={11}/> Portal
                    </a>
                  )}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap gap-2">
                <button onClick={() => setFilingClaim(p)} data-testid={`file-claim-btn-${p.id}`}
                  className="h-8 px-2 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-[10px] shadow-[2px_2px_0_#0f172a] inline-flex items-center gap-1">
                  <ShieldCheck size={11}/> File Claim
                </button>
                {p.warranty_contractor?.name && (
                  <button onClick={() => assignWarrantyVendor(p.id)} data-testid={`assign-warranty-btn-${p.id}`}
                    className="h-8 px-2 border-2 border-fuchsia-500 text-fuchsia-700 font-black uppercase tracking-widest text-[10px] hover:bg-fuchsia-50 inline-flex items-center gap-1">
                    Assign {p.warranty_contractor.name}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filingClaim && (
        <FileClaimModal
          jobId={jobId}
          policy={filingClaim}
          onClose={() => setFilingClaim(null)}
          onFiled={(claim) => { setFilingClaim(null); onClaimFiled && onClaimFiled(claim); load(); }}
        />
      )}
    </div>
  );
}


function FileClaimModal({ jobId, policy, onClose, onFiled }) {
  const [claimNumber, setClaimNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("filed");
  const [busy, setBusy] = useState(false);

  const file = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/insurance-claim`, {
        policy_id: policy.id, claim_number: claimNumber,
        status, notes,
      });
      toast.success(`Claim filed — job status now: ${r.data.new_status || "unchanged"}`);
      onFiled(r.data.claim);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="file-claim-modal">
      <div className="bg-white w-full max-w-md border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-red-600 text-white flex items-start justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">File Insurance Claim</div>
            <div className="text-base font-black uppercase tracking-widest">{policy.provider_name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-red-700">×</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Claim number</label>
            <input value={claimNumber} onChange={e => setClaimNumber(e.target.value)} data-testid="claim-number-input"
              className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm font-mono"
              placeholder="e.g. AWR-2026-12345"/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} data-testid="claim-status"
              className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm">
              <option value="filed">Filed — awaiting response</option>
              <option value="approved">Approved — provider dispatching</option>
              <option value="denied">Denied — need to dispatch our own tech</option>
              <option value="resolved">Resolved — closed out</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} data-testid="claim-notes"
              className="mt-1 w-full p-3 border-2 border-slate-300 text-sm"/>
          </div>
          <button onClick={file} disabled={busy} data-testid="claim-file-submit"
            className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Filing…" : "Attach to Job"}
          </button>
        </div>
      </div>
    </div>
  );
}
